import { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import "./FinishToggle.scss";

// key = internal id (matches window.DCoverType / applySequence logic)
// label = text shown to user
// sequence = window[...] variable name
const OPTIONS = [
    { key: "Metal", label: "Metal Design", sequence: "DCover_Metal" },
    { key: "Plastic", label: "Plastic Groove", sequence: "DCover_Plastic" },
];

/**
 * inline = false (default): global instance -- portals into the static
 *   #finishToggle div (used once in App.js for the onload bottom-left overlay).
 *   Controlled via window.handleFinishToggle(true/false).
 *
 * inline = true: renders directly wherever it's placed in the JSX tree --
 *   used inside a popup (e.g. the hotspot popup in SubmenuList.js) which is
 *   already portaled into #popupContainer, so no extra portal is needed.
 */
const FinishToggle = ({ inline = false }) => {
    const [selected, setSelected] = useState(() => {
        return window.DCoverType === "plastic" ? "Plastic" : "Metal";
    });

    const [isVisible, setIsVisible] = useState(true);

    const applySequence = (optionKey) => {
        const option = OPTIONS.find((o) => o.key === optionKey);

        if (
            option &&
            window.scene &&
            typeof window.scene.applySequence === "function"
        ) {
            window.scene.applySequence(window[option.sequence]);
        }
    };

    const handleSelect = (optionKey) => {
        if (optionKey === selected) return;

        setSelected(optionKey);

        window.DCoverType = optionKey === "Plastic" ? "plastic" : "metal";

        window.dispatchEvent(
            new CustomEvent("DCoverTypeChange", { detail: window.DCoverType })
        );

        applySequence(optionKey);
    };

    useEffect(() => {
        // keep this instance's local `selected` state in sync with window.DCoverType
        // no matter which instance (inline popup or global overlay) actually changed it
        const syncFromGlobal = (e) => {
            const val = e?.detail ?? window.DCoverType;
            setSelected(val === "plastic" ? "Plastic" : "Metal");
        };

        window.addEventListener("DCoverTypeChange", syncFromGlobal);
        return () => window.removeEventListener("DCoverTypeChange", syncFromGlobal);
    }, []);

    useEffect(() => {
        if (inline) return;

        // Expose global toggler
        window.handleFinishToggle = (value = false) => {
            if (value) {
                // extra safety: resync right when the overlay becomes visible,
                // in case it was hidden while another instance changed the value
                setSelected(window.DCoverType === "plastic" ? "Plastic" : "Metal");
            }
            setIsVisible(value);
        };

        return () => {
            delete window.handleFinishToggle;
        };
    }, [inline]);

    if (!inline && !isVisible) return null;

    const content = (
        <div className="finishToggle" onClick={(e) => e.stopPropagation()}>
            <span className="finishToggleLabel">Choose your finish</span>

            <div
                className="finishToggleGroup"
                role="radiogroup"
                aria-label="Finish"
            >
                {OPTIONS.map(({ key, label }) => (
                    <div
                        key={key}
                        className={`finishToggleOption ${key} ${
                            selected === key ? "active" : ""
                        }`}
                        onClick={() => handleSelect(key)}
                        role="radio"
                        tabIndex={0}
                        aria-checked={selected === key}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleSelect(key);
                            }
                        }}
                    >
                        {label}
                    </div>
                ))}
            </div>
        </div>
    );

    if (inline) {
        return content;
    }

    const portalRoot = document.getElementById("finishToggle");
    return portalRoot ? ReactDOM.createPortal(content, portalRoot) : null;
};

export default FinishToggle;